/**
 * Workspace Scan Profile Library — full analytics view.
 *
 * Renders a modal-style table listing every profile with performance metrics,
 * quality status chip, and per-row actions (Favorite / Clone / Archive).
 */
import { useEffect, useState } from "react";
import { Loader2, Star, Copy, Archive, X, TrendingUp, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const QUALITY_TONE = {
  "Untested":       "border-border/60 text-muted-foreground",
  "Collecting Data":"border-border/60 text-muted-foreground",
  "Experimental":   "border-signal-yellow/60 text-signal-yellow",
  "Promising":      "border-[#0A84FF] text-[#0A84FF]",
  "Validated":      "border-signal-green text-signal-green",
  "Deteriorating":  "border-signal-red text-signal-red",
};

function fmt(v, d = 2) {
  if (v == null || !isFinite(v)) return "N/A";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return Number(v).toFixed(d);
}
function pct(v, d = 1) {
  if (v == null || !isFinite(v)) return "N/A";
  return `${v >= 0 ? "+" : ""}${v.toFixed(d)}%`;
}

export default function ProfileLibraryModal({ open, onClose, onSelect }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/scanner/profiles/library");
      setRows(data.profiles || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  if (!open) return null;

  const toggleFav = async (p) => {
    try {
      await api.post(`/scanner/profiles/${p.id}/favorite`, {});
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
  };
  const clone = async (p) => {
    try {
      const { data } = await api.post(`/scanner/profiles/${p.id}/clone`, {});
      toast.success(`Cloned to '${data.name}'`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Clone failed"); }
  };
  const archive = async (p) => {
    try {
      await api.post(`/scanner/profiles/${p.id}/archive`, {});
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Archive failed"); }
  };

  const visible = rows.filter((p) => !p.is_archived);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={onClose}
         data-testid="profile-library-modal">
      <div className="w-full max-w-6xl max-h-[86vh] bg-background border border-border/60 overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 p-3 border-b border-border/60">
          <TrendingUp size={14} className="text-[#0A84FF]"/>
          <span className="mono uppercase text-[11px] tracking-widest">Scan Profile Library</span>
          <span className="mono text-[10px] text-muted-foreground">({visible.length} active)</span>
          <button className="btn-ghost h-7 !px-2 text-[10px] ml-auto" onClick={load} data-testid="library-refresh">
            <RefreshCw size={11}/> Refresh
          </button>
          <button className="btn-ghost h-7 !px-2 text-[10px]" onClick={onClose} data-testid="library-close">
            <X size={12}/>
          </button>
        </div>
        {loading ? (
          <div className="p-10 text-center text-muted-foreground text-xs">
            <Loader2 className="inline animate-spin mr-2" size={12}/> Computing analytics…
          </div>
        ) : (
          <div className="overflow-auto flex-1">
            <table className="w-full text-xs">
              <thead className="bg-card/60 sticky top-0 border-b border-border/60 text-[10px] uppercase mono text-muted-foreground tracking-widest">
                <tr>
                  <th className="p-2 text-left">Profile</th>
                  <th className="p-2 text-center">Ver</th>
                  <th className="p-2 text-right">Runs</th>
                  <th className="p-2 text-right">AI Cost</th>
                  <th className="p-2 text-right">Opps</th>
                  <th className="p-2 text-right">Trades</th>
                  <th className="p-2 text-right">Win %</th>
                  <th className="p-2 text-right">PF</th>
                  <th className="p-2 text-right">Avg Return</th>
                  <th className="p-2 text-right">Alpha vs SPY</th>
                  <th className="p-2 text-right">Edge/opp</th>
                  <th className="p-2 text-left" title="Workspace-level walk-forward robustness (not per profile)">WF*</th>
                  <th className="p-2 text-left" title="Workspace-level overfitting composite (not per profile)">Overfit*</th>
                  <th className="p-2 text-left">Quality</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((p) => {
                  const a = p.analytics || {};
                  const q = a.quality_status || "Untested";
                  return (
                    <tr key={p.id} className="border-b border-border/40 hover:bg-secondary/20"
                        data-testid={`library-row-${p.id}`}>
                      <td className="p-2">
                        <button className="mono text-left hover:text-[#0A84FF]"
                                onClick={() => { onSelect?.(p); onClose?.(); }}
                                data-testid={`library-select-${p.id}`}>
                          <div className="flex items-center gap-1">
                            {p.is_favorite && <Star size={10} className="text-signal-yellow fill-current"/>}
                            <span className="font-medium">{p.name}</span>
                          </div>
                          <div className="text-[10px] text-muted-foreground truncate max-w-[220px]">{p.description || "—"}</div>
                        </button>
                      </td>
                      <td className="p-2 mono text-center">v{p.version ?? 1}</td>
                      <td className="p-2 mono text-right">{a.times_run ?? 0}</td>
                      <td className="p-2 mono text-right">${fmt(a.ai_cost_usd, 3)}</td>
                      <td className="p-2 mono text-right">{a.opportunities_generated ?? 0}</td>
                      <td className="p-2 mono text-right">{a.trades_taken ?? 0}</td>
                      <td className="p-2 mono text-right">{a.win_rate_pct == null ? "N/A" : `${a.win_rate_pct.toFixed(0)}%`}</td>
                      <td className={`p-2 mono text-right ${a.profit_factor == null ? "" : a.profit_factor >= 1.3 ? "pnl-up" : a.profit_factor >= 1 ? "text-signal-yellow" : "pnl-down"}`}>
                        {fmt(a.profit_factor)}
                      </td>
                      <td className={`p-2 mono text-right ${a.avg_return_pct == null ? "" : a.avg_return_pct >= 0 ? "pnl-up" : "pnl-down"}`}>
                        {pct(a.avg_return_pct)}
                      </td>
                      <td className={`p-2 mono text-right ${a.alpha_vs_spy_pct == null ? "" : a.alpha_vs_spy_pct >= 0 ? "pnl-up" : "pnl-down"}`}>
                        {pct(a.alpha_vs_spy_pct)}
                      </td>
                      <td className={`p-2 mono text-right ${a.ai_edge_contribution == null ? "" : a.ai_edge_contribution >= 0 ? "pnl-up" : "pnl-down"}`}>
                        {a.ai_edge_contribution == null ? "N/A" : `$${a.ai_edge_contribution.toFixed(2)}`}
                      </td>
                      <td className="p-2 mono text-[10px] uppercase text-muted-foreground">{a.walk_forward_status || "N/A"}</td>
                      <td className="p-2 mono text-[10px] uppercase text-muted-foreground">{a.overfitting_status || "N/A"}</td>
                      <td className="p-2">
                        <span className={`chip !text-[9px] uppercase ${QUALITY_TONE[q]}`} data-testid={`library-quality-${p.id}`}>{q}</span>
                      </td>
                      <td className="p-2 text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <button className="text-muted-foreground hover:text-signal-yellow p-0.5" onClick={() => toggleFav(p)}
                                  title="Favorite" data-testid={`library-fav-${p.id}`}>
                            <Star size={11} className={p.is_favorite ? "text-signal-yellow fill-current" : ""}/>
                          </button>
                          <button className="text-muted-foreground hover:text-foreground p-0.5" onClick={() => clone(p)}
                                  title="Clone" data-testid={`library-clone-${p.id}`}>
                            <Copy size={11}/>
                          </button>
                          <button className="text-muted-foreground hover:text-signal-red p-0.5" onClick={() => archive(p)}
                                  title="Archive" data-testid={`library-archive-${p.id}`}>
                            <Archive size={11}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && (
          <div className="px-3 py-1.5 border-t border-border/40 text-[10px] text-muted-foreground mono">
            * WF and Overfit are workspace-level metrics (not per-profile). Per-profile walk-forward will land when profile-scoped WF configs ship.
          </div>
        )}
      </div>
    </div>
  );
}
